import { LitElement, html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

import translate from '../../utils/webcomponents/translate';
import watchlistEvents from './watchlist-events';

import './watchlist-item';

const inactiveClass = 'is-inactive';

const tableHeader = (showIsinColumn) => html`
	<div class="table__header">
		<div class="table__heading manageable-list__item-action"></div>
		${showIsinColumn ? html`<div class="table__heading">${translate('watchlist', 'isin', 'ISIN')}</div>` : ''}
		<div class="table__heading">${translate('watchlist', 'name', 'Name')}</div>
		<div class="table__heading text-right">${translate('watchlist', 'stoploss', 'Stop loss')}</div>
		<div class="table__heading  text-right">${translate('watchlist', 'bid', 'Bid')}</div>
		<div class="table__heading text-right">${translate('watchlist', 'ask', 'Ask')}</div>
		<div class="table__heading text-right">${translate('watchlist', 'performance', 'Performance')}</div>
	</div>
`;

class WatchlistTable extends LitElement {
	static get properties() {
		return {
			items: { type: Array },
			showIsinColumn: { type: Boolean },
		};
	}

	render() {
		return html`
			<div class="table-container">
				<div class="table">
					${tableHeader(this.showIsinColumn)}

					<div class="table__body">
						${repeat(
							this.items,
							(item) => item.isin,
							(item) => html`
								<x-watchlist-item
									class="table__row manageable-list__item ${item.isProductInactive
										? inactiveClass
										: ''}"
									id="${item.isin}"
									.data="${item}"
									?showIsinColumn="${this.showIsinColumn}"
								>
								</x-watchlist-item>
							`,
						)}
					</div>
				</div>
			</div>
		`;
	}

	updated() {
		super.updated();

		if (!this.isDraggable && this.items.length) {
			this.setDraggable();
			this.isDraggable = true;
		}
	}

	setDraggable() {
		const domItems = this.querySelectorAll(`x-watchlist-item:not(.${inactiveClass})`);

		domItems.forEach((el) => {
			// Wait for watchlist-items to render
			el.updateComplete.then(() => {
				const dragButton = el.querySelector('.button--drag');

				// Enable watchlist-item drag only when mouse over drag button
				dragButton.addEventListener('mouseover', () => {
					el.setAttribute('draggable', true);
				});
				dragButton.addEventListener('mouseout', () => {
					el.setAttribute('draggable', false);
				});

				el.addEventListener('dragstart', this.dragStart.bind(this));
				el.addEventListener('drop', this.dragStop.bind(this));
				el.addEventListener('dragenter', this.cancelDefault.bind(this));
				el.addEventListener('dragover', this.cancelDefault.bind(this));
			});
		});
	}

	dragStart(e) {
		const domElementIds = [...document.querySelectorAll('x-watchlist-item')].map((t) => t.id);
		const startIndex = domElementIds.indexOf(e.currentTarget.id);

		e.dataTransfer.setData('text/plain', startIndex);
	}

	cancelDefault(e) {
		e.preventDefault();
		e.stopPropagation();

		return false;
	}

	dispatchCustomEvent(eventName, detail = {}) {
		const event = new CustomEvent(eventName, {
			bubbles: true,
			detail,
		});

		this.dispatchEvent(event);
	}

	getWatchlistElements() {
		return Array.from(document.querySelectorAll('x-watchlist-item'));
	}

	dragStop(e) {
		this.cancelDefault(e);
		const domElements = this.getWatchlistElements();
		const startIndex = Number(e.dataTransfer.getData('text/plain'));
		const endIndex = domElements.map((t) => t.id).indexOf(e.currentTarget.id);

		if (startIndex !== endIndex) {
			const draggedElement = domElements[startIndex];
			const insertBeforeElementIdx = startIndex < endIndex ? endIndex + 1 : endIndex;
			const insertBeforeElement = domElements[insertBeforeElementIdx];
			const target = domElements[endIndex];
			const dragElements = [draggedElement, target];

			this.setIsDragging(dragElements, true);
			draggedElement.parentElement.insertBefore(draggedElement, insertBeforeElement);
			this.setIsDragging(dragElements, false);

			// Update order property to match visual index
			this.getWatchlistElements().forEach((item, index) => {
				item.data.order = index;
			});

			this.dispatchCustomEvent(watchlistEvents.ITEM_MOVE);
		}
	}

	setDraggeable() {
		const domItems = this.querySelectorAll('x-watchlist-item');

		domItems.forEach((el) => {
			el.setAttribute('draggable', true);
			el.addEventListener('dragstart', this.dragStart);
			el.addEventListener('drop', this.dragStop.bind(this));
			el.addEventListener('dragenter', this.cancelDefault);
			el.addEventListener('dragover', this.cancelDefault);
		});
	}

	// Set isDragging flag
	setIsDragging(items, value) {
		items?.forEach((item) => {
			item.isDragging = value;
		});
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-watchlist-table', WatchlistTable);

export default WatchlistTable;
